import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  PaperAirplaneIcon,
  CurrencyRupeeIcon,
  ExclamationTriangleIcon,
  BriefcaseIcon,
  SparklesIcon,
  CalendarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { User } from '../../types';
import Button from '../common/Button';
import DatePicker from '../common/DatePicker';
import { collaborationAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { createPortal } from 'react-dom';
import Avatar from '../common/Avatar';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import LocationPicker from '../common/LocationPicker';
import { formatCurrency } from '../../utils/format';
import { ServicePackage } from '../../types/service';
import ServicePackageDetailsModal from '../profile/ServicePackageDetailsModal';
import api from '../../services/api';

interface CollaborationRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  receiver: User;
  onSuccess?: () => void;
  initialValues?: {
    type?: string;
    budget?: number;
    subject?: string;
    message?: string;
    is_paid?: boolean;
    duration?: string;
    timeline?: string;
    service_package_id?: string;
    service_package?: ServicePackage;
  };
  isBooking?: boolean;
}

const COLLABORATION_TYPES = [
  { value: 'portrait', label: 'Portrait Photography', icon: '👤' },
  { value: 'fashion', label: 'Fashion Photography', icon: '👗' },
  { value: 'event', label: 'Event Photography', icon: '🎉' },
  { value: 'commercial', label: 'Commercial Photography', icon: '🏢' },
  { value: 'lifestyle', label: 'Lifestyle Photography', icon: '🌟' },
  { value: 'tfp', label: 'Time for Portfolio (TFP)', icon: '📸' },
  { value: 'other', label: 'Other', icon: '✨' },
];

const TIMELINE_OPTIONS = [
  { value: 'immediate', label: 'ASAP (within 24 hours)', urgency: 'urgent' },
  { value: 'this_week', label: 'This week', urgency: 'high' },
  { value: 'this_month', label: 'This month', urgency: 'normal' },
  { value: 'flexible', label: 'I\'m flexible', urgency: 'low' },
];

const CollaborationRequestModal: React.FC<CollaborationRequestModalProps> = ({
  isOpen,
  onClose,
  receiver,
  onSuccess,
  initialValues,
  isBooking = false
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  
  const { user: currentUser } = useSelector((state: RootState) => state.auth);
  
  const [packages, setPackages] = useState<ServicePackage[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [viewingPackage, setViewingPackage] = useState<ServicePackage | null>(null);

  const [formData, setFormData] = useState({
    type: initialValues?.type || '',
    timeline: initialValues?.timeline || '',
    subject: initialValues?.subject || '',
    message: initialValues?.message || '',
    location: '',
    duration: initialValues?.duration || '',
    budget: initialValues?.budget ? initialValues.budget.toString() : '',
    is_paid: initialValues?.is_paid !== undefined ? initialValues.is_paid : true,
    preferred_dates: [''],
  });

  // Fetch packages on open
  useEffect(() => {
    if (isOpen && receiver) {
      setLoadingPackages(true);
      api.get(`/services/user/${receiver.id}?page=1&limit=20`)
        .then(res => {
          if (res.data.success) {
            const fetchedPackages = res.data.data || [];
            setPackages(fetchedPackages);
            
            // Direct package ID booking
            if (initialValues?.service_package_id && fetchedPackages.length > 0) {
              const matchedPkg = fetchedPackages.find((p: ServicePackage) => p.id === initialValues.service_package_id);
              if (matchedPkg) {
                const defaultLocation = matchedPkg.locations?.[0] || receiver.location || 'TBD';
                setSelectedPackageId(matchedPkg.id);
                setFormData(prev => ({
                  ...prev,
                  type: matchedPkg.category || 'other',
                  subject: `Booking Request: ${matchedPkg.title}`,
                  budget: matchedPkg.price.toString(),
                  is_paid: matchedPkg.price > 0,
                  location: defaultLocation,
                }));
                setCurrentStep(2);
              }
            } else if (initialValues?.subject && initialValues.subject.startsWith('Booking Request:') && fetchedPackages.length > 0) {
              // Try to find matching package by subject
              const titleMatch = initialValues.subject.replace('Booking Request: ', '');
              const matchedPkg = fetchedPackages.find((p: ServicePackage) => p.title === titleMatch);
              if (matchedPkg) {
                setSelectedPackageId(matchedPkg.id);
                setFormData(prev => ({ ...prev, type: matchedPkg.category || 'other' }));
              }
            } else if (fetchedPackages.length === 0) {
              // No packages available, default to custom
              setSelectedPackageId('custom');
            }
          }
        })
        .finally(() => setLoadingPackages(false));
    } else {
      // Reset state on close
      setCurrentStep(1);
      setSelectedPackageId(null);
    }
  }, [isOpen, receiver, initialValues?.service_package_id, initialValues?.subject]);

  // Update form data when initialValues change
  useEffect(() => {
    if (isOpen && initialValues) {
      setFormData(prev => ({
        ...prev,
        type: initialValues.type || prev.type,
        timeline: initialValues.timeline || prev.timeline,
        subject: initialValues.subject || prev.subject,
        message: initialValues.message || prev.message,
        budget: initialValues.budget ? initialValues.budget.toString() : prev.budget,
        is_paid: initialValues.is_paid !== undefined ? initialValues.is_paid : prev.is_paid,
        duration: initialValues.duration || prev.duration,
      }));
      
      if (initialValues.service_package_id) {
         setSelectedPackageId(initialValues.service_package_id);
         setCurrentStep(2);
      } else if (initialValues.type && !initialValues.subject?.startsWith('Booking Request:')) {
         setSelectedPackageId('custom');
      }
    }
  }, [isOpen, initialValues]);

  // Pre-fill fields when a package is selected
  useEffect(() => {
    if (selectedPackageId && selectedPackageId !== 'custom') {
      const pkg = packages.find(p => p.id === selectedPackageId);
      if (pkg) {
        setFormData(prev => ({
          ...prev,
          budget: pkg.price.toString(),
          subject: `Booking Request: ${pkg.title}`,
          is_paid: pkg.price > 0,
        }));
      }
    } else if (selectedPackageId === 'custom') {
      // Clear if switching to custom, only if not already having initial values (excluding bookings)
      const isBookingRequest = initialValues?.subject?.startsWith('Booking Request:');
      setFormData(prev => ({
        ...prev,
        budget: isBookingRequest ? '' : (initialValues?.budget ? initialValues.budget.toString() : ''),
        subject: isBookingRequest ? '' : (initialValues?.subject ? initialValues.subject : ''),
        message: isBookingRequest ? '' : (initialValues?.message ? initialValues.message : ''),
        type: isBookingRequest ? '' : (initialValues?.type ? initialValues.type : ''),
      }));
    }
  }, [selectedPackageId, packages, initialValues]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addPreferredDate = () => {
    setFormData(prev => ({
      ...prev,
      preferred_dates: [...prev.preferred_dates, '']
    }));
  };

  const removePreferredDate = (index: number) => {
    setFormData(prev => ({
      ...prev,
      preferred_dates: prev.preferred_dates.filter((_, i) => i !== index)
    }));
  };

  const updatePreferredDate = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      preferred_dates: prev.preferred_dates.map((date, i) => i === index ? value : date)
    }));
  };

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 1:
        return selectedPackageId !== null && (selectedPackageId !== 'custom' || formData.type);
      case 2:
        return (initialValues?.service_package_id || (selectedPackageId && selectedPackageId !== 'custom') || formData.subject) && formData.message && formData.timeline;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (!canProceedToNextStep()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const selectedTimeline = TIMELINE_OPTIONS.find(t => t.value === formData.timeline);
      
      const requestData = {
        receiver_id: receiver.id,
        type: formData.type || 'other',
        timeline: formData.timeline,
        urgency: selectedTimeline?.urgency || 'normal',
        subject: formData.subject,
        message: formData.message,
        location: formData.location || '',
        duration: formData.duration || '',
        budget: formData.budget ? parseFloat(formData.budget) : 0,
        is_paid: formData.is_paid,
        preferred_dates: formData.preferred_dates.filter(date => date.trim() !== ''),
        // Use selectedPackageId from the selection UI, or fall back directly to initialValues.service_package_id
        // This ensures the package ID is always sent even if the matchedPkg lookup failed
        service_package_id: (selectedPackageId && selectedPackageId !== 'custom')
          ? selectedPackageId
          : (initialValues?.service_package_id || undefined),
      };

      await collaborationAPI.sendCollaborationRequest(requestData);
      
      toast.success('Collaboration request sent successfully!');
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Failed to send collaboration request:', error);
      toast.error(error.response?.data?.error || 'Failed to send request');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepIndicator = () => {
    if (initialValues?.service_package_id) return null;
    return (
      <div className="flex items-center justify-center mb-6">
        <div className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-800 p-1.5 rounded-full">
          <div className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-300 ${currentStep === 1 ? 'bg-white dark:bg-gray-600 text-black dark:text-white shadow-sm' : 'text-gray-500'}`}>
            1. Options
          </div>
          <div className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-300 ${currentStep === 2 ? 'bg-white dark:bg-gray-600 text-black dark:text-white shadow-sm' : 'text-gray-500'}`}>
            2. Details
          </div>
        </div>
      </div>
    );
  };

  const renderStep1 = () => (
    <motion.div 
      initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
          <SparklesIcon className="w-6 h-6 mr-2 text-primary-500" />
          Choose Collaboration Type
        </h3>
        
        {loadingPackages ? (
          <div className="flex justify-center py-12">
             <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {packages.length > 0 ? (
              <>
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Available Packages</p>
                  <div className="grid grid-cols-1 gap-3">
                    {packages.map(pkg => (
                      <div
                        key={pkg.id}
                        onClick={() => setViewingPackage(pkg)}
                        className={`relative p-5 rounded-2xl border cursor-pointer transition-all duration-300 overflow-hidden ${
                          selectedPackageId === pkg.id
                            ? 'border-gray-900 bg-gray-50 dark:bg-gray-800 dark:border-white shadow-md ring-1 ring-gray-900 dark:ring-white scale-[1.01]'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-sm bg-white dark:bg-gray-800/50'
                        }`}
                      >
                        <div className="flex justify-between items-start gap-4">
                           <div className="flex-1">
                             <h4 className="font-bold text-gray-900 dark:text-white text-lg">{pkg.title}</h4>
                             <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5 line-clamp-2">{pkg.description}</p>
                           </div>
                           <div className="text-right shrink-0 flex flex-col items-end">
                             <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 mb-2 shadow-sm">
                               {formatCurrency(pkg.price)}
                             </span>
                             <div className="text-xs text-gray-500 flex items-center font-medium">
                               <ClockIcon className="w-3.5 h-3.5 mr-1 text-gray-400" /> {pkg.delivery_time_days} Days
                             </div>
                           </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3 pt-2">
                  <p className="text-sm font-semibold text-gray-500 dark:text-gray-300 uppercase tracking-wider">Or Propose a Custom Idea</p>
                  <div
                    onClick={() => setSelectedPackageId('custom')}
                    className={`relative p-5 rounded-2xl border cursor-pointer transition-all duration-300 ${
                      selectedPackageId === 'custom'
                        ? 'border-gray-900 bg-gray-50 dark:bg-gray-800 dark:border-white shadow-md ring-1 ring-gray-900 dark:ring-white scale-[1.01]'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-sm bg-white dark:bg-gray-800/50'
                    }`}
                  >
                    <div className="flex items-center">
                      <div className={`p-4 rounded-xl mr-5 transition-colors ${selectedPackageId === 'custom' ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900 shadow-md' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
                        <BriefcaseIcon className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 dark:text-white text-lg">Custom Request</h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Send a personalized proposal with your own budget and timeline details.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                 <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                   This creator hasn't published any predefined packages. You can propose a custom collaboration below!
                 </p>
              </div>
            )}

            {/* Custom Options expansion */}
            <AnimatePresence>
              {(selectedPackageId === 'custom' || packages.length === 0) && (
                <motion.div 
                  initial={{ opacity: 0, height: 0, y: -10 }} animate={{ opacity: 1, height: 'auto', y: 0 }} exit={{ opacity: 0, height: 0, y: -10 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className={`${packages.length > 0 ? 'mt-6' : ''} overflow-hidden bg-gray-50 dark:bg-gray-800/50 p-5 rounded-2xl border border-gray-100 dark:border-gray-800`}
                >
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Specify Collaboration Category
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {COLLABORATION_TYPES.map((type) => (
                      <label
                        key={type.value}
                        className={`relative flex items-center p-3 rounded-xl border cursor-pointer transition-colors ${
                          formData.type === type.value
                            ? 'border-gray-900 bg-white ring-1 ring-gray-900 dark:ring-white dark:border-white shadow-sm dark:bg-gray-700'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                        }`}
                      >
                        <input
                          type="radio"
                          name="type"
                          value={type.value}
                          checked={formData.type === type.value}
                          onChange={(e) => handleInputChange('type', e.target.value)}
                          className="sr-only"
                        />
                        <div className="flex items-center space-x-3">
                          <span className="text-xl bg-gray-50 dark:bg-gray-700 p-1.5 rounded-lg">{type.icon}</span>
                          <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">{type.label}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </motion.div>
  );

  const renderStep2 = () => (
    <motion.div 
       initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
       className="space-y-6"
    >
      <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-800/50 dark:to-gray-800/20 p-6 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-5 flex items-center">
          <CalendarIcon className="w-6 h-6 mr-2 text-primary-500" />
          Schedule & Timeline
        </h3>
        
        {/* Preferred Dates */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            Preferred Dates (Calendar)
          </label>
          <div className="space-y-3">
            {formData.preferred_dates.map((date, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div className="flex-1 bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 focus-within:ring-2 focus-within:ring-black dark:focus-within:ring-white transition-all overflow-hidden">
                  <DatePicker
                    selectedDate={date || null}
                    onChange={(newDate) => updatePreferredDate(index, newDate)}
                    placeholder="Select a date"
                  />
                </div>
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => removePreferredDate(index)}
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addPreferredDate}
              className="text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 hover:border-gray-900 dark:hover:border-white font-medium flex items-center mt-3 px-3 py-1.5 rounded-lg transition-colors shadow-sm"
            >
              + Add another option
            </button>
          </div>
        </div>

         {/* Timeline Urgency */}
         <div className="mb-2">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              How soon does this need to happen? *
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {TIMELINE_OPTIONS.map((timeline) => (
                 <div
                   key={timeline.value}
                   onClick={() => handleInputChange('timeline', timeline.value)}
                   className={`p-3 rounded-xl border text-sm cursor-pointer transition-all duration-200 text-center ${
                     formData.timeline === timeline.value
                       ? 'border-gray-900 bg-gray-900 text-white dark:bg-white dark:text-gray-900 font-bold shadow-md scale-105 z-10'
                       : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 hover:shadow-sm'
                   }`}
                 >
                   <div className="flex flex-col items-center justify-center h-full">
                     <span>{timeline.label}</span>
                     {timeline.urgency === 'urgent' && <ExclamationTriangleIcon className={`h-4 w-4 mt-1 ${formData.timeline === timeline.value ? 'text-red-400' : 'text-red-500'}`} />}
                   </div>
                 </div>
              ))}
            </div>
         </div>
      </div>

      <div className="space-y-5 px-1 py-2">
        {/* Subject (hidden if direct package booking) */}
        {!(initialValues?.service_package_id || (selectedPackageId && selectedPackageId !== 'custom')) && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Subject *</label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              placeholder="e.g., Let's collaborate on..."
              className="w-full px-4 py-3.5 border border-gray-200 dark:border-gray-700 bg-gray-50 focus:bg-white dark:bg-gray-800/50 dark:focus:bg-gray-800 rounded-xl focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent text-gray-900 dark:text-white transition-all shadow-sm"
              required
            />
          </div>
        )}

        {/* Message */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            {(initialValues?.service_package_id || (selectedPackageId && selectedPackageId !== 'custom')) ? 'Message / Additional Notes *' : 'Message *'}
          </label>
          <textarea
            value={formData.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
            placeholder="Tell them about your vision and what you'd like to create together..."
            rows={4}
            className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 bg-gray-50 focus:bg-white dark:bg-gray-800/50 dark:focus:bg-gray-800 rounded-xl focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent text-gray-900 dark:text-white transition-all shadow-sm resize-none"
            required
          />
        </div>

        {/* Budget & Location (hidden if direct package booking) */}
        {!(initialValues?.service_package_id || (selectedPackageId && selectedPackageId !== 'custom')) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Budget */}
            <div>
               <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Budget (if paid)</label>
              <div className="relative shadow-sm rounded-xl overflow-hidden group">
                  <CurrencyRupeeIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-black dark:group-focus-within:text-white transition-colors" />
                  <input
                    type="number"
                    value={formData.budget}
                    onChange={(e) => handleInputChange('budget', e.target.value)}
                    disabled={selectedPackageId !== 'custom' && selectedPackageId !== null}
                    placeholder="0"
                    className={`w-full pl-11 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent text-gray-900 dark:text-white transition-all ${
                      selectedPackageId !== 'custom' && selectedPackageId !== null ? 'bg-gray-100 dark:bg-gray-800 text-gray-500 cursor-not-allowed border-transparent' : 'bg-gray-50 focus:bg-white dark:bg-gray-800/50 dark:focus:bg-gray-800'
                    }`}
                  />
               </div>
            </div>
            
            {/* Location */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Location</label>
              <div className="bg-gray-50 focus-within:bg-white dark:bg-gray-800/50 dark:focus-within:bg-gray-800 rounded-xl shadow-sm transition-all border border-gray-200 dark:border-gray-700 relative group">
                <LocationPicker
                  value={formData.location}
                  onChange={(loc) => handleInputChange('location', loc)}
                  placeholder="Where will this happen?"
                  className="w-full bg-transparent border-none py-3 focus:ring-0 text-gray-900 dark:text-white"
                  saveCoordinates={false}
                />
                {/* Optional: Add a focus ring overlay to match other inputs */}
                <div className="absolute inset-0 rounded-xl pointer-events-none group-focus-within:ring-2 group-focus-within:ring-black dark:group-focus-within:ring-white transition-all"></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );

  if (!isOpen) return null;

  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot) return null;

  const modalContent =  (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="bg-white dark:bg-gray-900 md:rounded-3xl shadow-2xl w-full md:max-w-2xl h-full md:h-auto md:max-h-[92vh] overflow-hidden relative z-[10000] flex flex-col border border-gray-100 dark:border-gray-800"
      >
        {/* Header */}
        <div className="p-5 md:p-6 border-b border-gray-100 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl shrink-0 z-10 sticky top-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Avatar
                  src={receiver.profile_pic_thumb_signed || receiver.profile_pic}
                  alt={`${receiver.first_name} ${receiver.last_name}`}
                  size="lg"
                  className="w-14 h-14 ring-2 ring-gray-100 dark:ring-gray-800"
                  isDeleted={!!receiver.deleted_at || !!receiver.deletion_scheduled_at}
                />
                <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-white dark:border-gray-900 rounded-full"></div>
              </div>
              <div>
                <p className="text-sm font-semibold text-primary-600 dark:text-primary-400 mb-0.5 tracking-wide uppercase">New Request</p>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white leading-none">
                  Collaborate with {receiver.first_name}
                </h2>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all text-gray-400 hover:text-gray-900 dark:hover:text-white active:scale-95"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 md:p-8 overflow-y-auto flex-1 custom-scrollbar">
          {renderStepIndicator()}
          
          <AnimatePresence mode="wait">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="p-5 md:p-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 flex flex-col-reverse md:flex-row justify-between gap-3 shrink-0">
          <div className="w-full md:w-auto">
            {currentStep > 1 && !initialValues?.service_package_id ? (
              <Button
                variant="outline"
                onClick={() => setCurrentStep(currentStep - 1)}
                className="w-full md:w-auto justify-center py-3 px-6 rounded-xl border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-800 shadow-sm"
              >
                Back
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={onClose}
                className="w-full md:w-auto justify-center py-3 px-6 rounded-xl border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-800 shadow-sm"
              >
                Cancel
              </Button>
            )}
          </div>
          
          <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
            {currentStep === 1 && !initialValues?.service_package_id && (
              <Button
                variant="outline"
                onClick={onClose}
                className="w-full md:w-auto justify-center py-3 px-6 rounded-xl border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-800 shadow-sm order-last md:order-first"
              >
                Cancel
              </Button>
            )}
            
            {currentStep < 2 ? (
              <Button
                variant="primary"
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={!canProceedToNextStep()}
                className="w-full md:w-auto justify-center py-3 px-8 rounded-xl shadow-lg shadow-black/10 transition-transform active:scale-95 text-base"
              >
                Continue
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleSubmit}
                loading={submitting}
                disabled={!canProceedToNextStep()}
                className="flex items-center justify-center space-x-2 w-full md:w-auto py-3 px-8 rounded-xl shadow-lg shadow-black/10 transition-transform active:scale-95 text-base bg-gray-900 hover:bg-black dark:bg-white dark:hover:bg-gray-100"
              >
                <PaperAirplaneIcon className="h-5 w-5" />
                <span>Send Request</span>
              </Button>
            )}
          </div>
        </div>
      </motion.div>
      {viewingPackage && (
        <ServicePackageDetailsModal
          isOpen={!!viewingPackage}
          onClose={() => setViewingPackage(null)}
          packageData={viewingPackage}
          isOwner={false}
          onBook={(pkg) => {
            setSelectedPackageId(pkg.id);
            setFormData(prev => ({
              ...prev,
              type: pkg.category || 'other',
              subject: `Booking Request: ${pkg.title}`,
              budget: pkg.price.toString(),
              is_paid: pkg.price > 0,
            }));
            setCurrentStep(2);
            setViewingPackage(null);
          }}
        />
      )}
    </div>
  );
  
  return createPortal(modalContent, modalRoot);
};

export default CollaborationRequestModal;
